/**
 * The footer the three run-settings dialogs show when they were opened by a
 * Run Tests, Create Tests or Resume Tests button rather than from a list
 * (#2175 slice 2, renamed in #2181).
 *
 * **Why a shared component.** The three dialogs are otherwise unrelated —
 * different fields, different validation, different save calls — but this
 * footer is the same offer in all three, and it is the part an operator
 * learns once and then expects everywhere. Three copies of two buttons is
 * how one of them ends up saying something slightly different.
 *
 * **What the two buttons mean.** Run uses what is on screen for this run
 * only and leaves the saved settings alone. Save and run keeps the changes
 * first. That pairing is the answer to the problem #2175 exists for: before
 * it, running once with something different meant making another template,
 * and the list grew every time anyone wanted a one-off.
 */
import { Play } from 'lucide-react'
import { Button, DialogFooter } from '@/components/ui'

export interface RunTemplateLaunchFooterProps {
  /** Disables both actions while a save or a start is in flight. */
  busy: boolean
  /** Blocks both actions when the form is not complete enough to run. */
  canRun: boolean
  onCancel: () => void
  /** Run with what is on screen, saving nothing. */
  onRun: () => void
  /** Save what is on screen, then run with it. */
  onSaveAndRun: () => void
}

export function RunTemplateLaunchFooter({
  busy,
  canRun,
  onCancel,
  onRun,
  onSaveAndRun,
}: RunTemplateLaunchFooterProps) {
  return (
    <DialogFooter>
      <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
      {/* The hint sits on a wrapping span, not on the button. A disabled
          button ignores pointer events, so a `title` on it never appears —
          which is how a tooltip meant to explain WHY a control is
          unavailable ends up unreachable exactly when it is needed
          (PR #2170). The span is not disabled and still shows it. */}
      <span title="Keep these changes, then start the run">
        <Button variant="outline" onClick={onSaveAndRun} disabled={busy || !canRun}>
          Save and run
        </Button>
      </span>
      <span title="Use these settings for this run only — what you have saved stays as it is">
        <Button onClick={onRun} disabled={busy || !canRun}>
          <Play className="h-3.5 w-3.5 mr-1.5" />
          Run
        </Button>
      </span>
    </DialogFooter>
  )
}
